import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Zap } from "lucide-react";

const SAMPLE_JSON = JSON.stringify({
  provider: "cricheroes",
  match_url: "https://cricheroes.com/matches/abc123",
  team_a: {name: "Star XI", score: "192/6", overs: "20"},
  team_b: {name: "Falcons", score: "148/8", overs: "20"},
  result: "Star XI won by 44 runs",
  mvp: "Rohit Sharma"
}, null, 2);

export default function AdminSportsIngest() {
  const [form, setForm] = useState({
    provider: "cricheroes",
    tournament_id: "",
    match_id: "",
    score_a: "",
    score_b: "",
    winner_id: "",
    result_text: "",
    raw_json: SAMPLE_JSON,
  });
  const [result, setResult] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);

  useEffect(() => {
    api.get("/admin/sports-webhook-info").then((r) => setWebhookInfo(r.data));
  }, []);

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const submit = async () => {
    try {
      let parsed;
      try { parsed = JSON.parse(form.raw_json || "{}"); } catch { parsed = { raw: form.raw_json }; }
      const payload = {
        provider: form.provider,
        raw_json: parsed,
      };
      if (form.tournament_id) payload.tournament_id = form.tournament_id;
      if (form.match_id) payload.match_id = form.match_id;
      if (form.score_a) payload.score_a = form.score_a;
      if (form.score_b) payload.score_b = form.score_b;
      if (form.winner_id) payload.winner_id = form.winner_id;
      if (form.result_text) payload.result_text = form.result_text;
      const { data } = await api.post("/admin/sports-ingest", payload);
      setResult(data);
      toast.success(data.updated_match ? "Match updated + winner advanced" : "Payload logged");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-sports-ingest" className="max-w-3xl">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Manual Sync · CricHeroes / Hudle</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-4">Sports Ingest.</h1>
      <p className="text-white/60 max-w-2xl mb-8 text-sm">
        Neither CricHeroes nor Hudle expose a stable public API. Paste an exported match payload here — the system logs it and, if you provide a Match ID + winner, will auto-update the tournament bracket.
      </p>

      <div className="border border-white/12 bg-[#131A2B] p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4">
            <option value="cricheroes">CricHeroes</option>
            <option value="hudle">Hudle</option>
            <option value="custom">Custom / Other</option>
          </select>
          <input value={form.match_id} onChange={(e) => setForm({ ...form, match_id: e.target.value })} placeholder="Tournament Match ID (from /admin/tournaments/:id)" data-testid="ingest-match-id" className="bg-transparent border border-white/20 py-3 px-4 font-mono text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.score_a} onChange={(e) => setForm({ ...form, score_a: e.target.value })} placeholder="Team A score (e.g. 192/6)" data-testid="ingest-score-a" className="bg-transparent border border-white/20 py-3 px-4" />
          <input value={form.score_b} onChange={(e) => setForm({ ...form, score_b: e.target.value })} placeholder="Team B score" data-testid="ingest-score-b" className="bg-transparent border border-white/20 py-3 px-4" />
        </div>
        <input value={form.winner_id} onChange={(e) => setForm({ ...form, winner_id: e.target.value })} placeholder="Winner Team ID (paste from tournament page)" data-testid="ingest-winner" className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-xs" />
        <input value={form.result_text} onChange={(e) => setForm({ ...form, result_text: e.target.value })} placeholder="Result text (e.g. Won by 44 runs)" className="w-full bg-transparent border border-white/20 py-3 px-4" />
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Full JSON payload (optional)</div>
          <textarea value={form.raw_json} onChange={(e) => setForm({ ...form, raw_json: e.target.value })} rows={10} data-testid="ingest-json" className="w-full bg-black border border-white/12 py-3 px-4 font-mono text-xs text-brand-blue" />
        </div>
        <button onClick={submit} data-testid="ingest-submit" className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest hover:glow-blue">
          <Zap size={14} className="inline mr-2" /> Ingest & Update
        </button>
      </div>

      {result && (
        <div className="mt-6 border border-brand-orange/40 bg-[#131A2B] p-6">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-2">Result</div>
          <pre className="text-xs text-white/80 whitespace-pre-wrap font-mono">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}

      {webhookInfo && (
        <div className="mt-10 border border-white/12 bg-[#131A2B] p-6" data-testid="webhook-info">
          <div className="font-mono text-xs uppercase tracking-widest brand-blue mb-3">// Partner Webhook URLs</div>
          <p className="text-sm text-white/60 mb-4">
            When CricHeroes/Hudle (or your own scraper) is ready to push data automatically, point them at these URLs.
            Payload format: JSON with optional <code className="text-brand-orange">match_id</code>, <code className="text-brand-orange">winner_id</code>, <code className="text-brand-orange">score_a</code>, <code className="text-brand-orange">score_b</code>, <code className="text-brand-orange">result_text</code>.
          </p>
          <div className="space-y-2 mb-4">
            {Object.entries(webhookInfo.endpoints).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="font-mono text-xs uppercase tracking-widest text-white/40 w-24">{k}</span>
                <code className="flex-1 font-mono text-xs text-brand-blue bg-black px-3 py-2 break-all">{v}</code>
                <button onClick={() => copy(v)} data-testid={`copy-${k}`} className="p-2 border border-white/20 hover:border-brand-orange"><Copy size={12} /></button>
              </div>
            ))}
          </div>
          <div className="text-xs font-mono text-white/50">
            Auth: {webhookInfo.token_required
              ? <span className="brand-orange">Requires header <code>X-Sports-Token: &lt;SPORTS_WEBHOOK_TOKEN&gt;</code></span>
              : <span className="text-yellow-400">Currently open (dev). Set <code>SPORTS_WEBHOOK_TOKEN</code> in backend/.env for production.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
